import React, { useCallback, useEffect, useMemo, useState } from "react";
import { writeMessageActions } from "../../_store";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import translations from '../../config/TranslateFile.jsx'


const CategoryCheckbox = React.memo(({ id, label, checked, onChange }) => (
  <div className="form-check d-flex align-items-center mt-2 mb-2">
    <input
      className="form-check-input me-2"
      type="checkbox"
      checked={checked}
      id={id}
      onChange={onChange}
    />
    <label
      className={`form-check-label fs-5 form-label-custom-visible ECard-checkbox`}
      htmlFor={id}
    >
      {label}
    </label>
  </div>
));

const EcardGallery = ({ EcardInmateID = null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedCategories: selectedCategoriesFromRedux } = useSelector(
    (state) => state.WriteMessage
  );
  const currentLang = translations();
  const [categories, setCategories] = useState([]);
  const [ecardImages, setEcardImages] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEcardsCount, setTotalEcardsCount] = useState(0);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isloading, setIsLoading] = useState(false);

  const rowCountPerPage = 8;


  // Load E-card categories
  useEffect(() => {
    let isMounted = true;
    showLoader();
    dispatch(writeMessageActions.LoadEcardCategoryList())
      .then((res) => {
        if (isMounted) {
          hideLoader();
          setCategories(res?.payload || []);
        }
      })
      .catch((error) => {
        hideLoader();
        if (isMounted) {
          console.error("Failed to load eCard categories:", error);
          setCategories([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  // Set default or Redux selected categories after categories load
  useEffect(() => {
    if (categories.length === 0) return;

    const allCategoryIds = categories
      .filter((cat) => cat.label !== "-- Select --")
      .map((cat) => cat.CategoryId);

    const defaultAll = [0, ...allCategoryIds];

    // If Redux has values, use them. Else initialize and dispatch.
    if (selectedCategoriesFromRedux?.length > 0) {
      setSelectedCategories(selectedCategoriesFromRedux);
    } else {
      setSelectedCategories(defaultAll);
      dispatch(writeMessageActions.setSelectedCategories(defaultAll));
    }
  }, [categories]);


  const handleCheckboxChange = useCallback(
    (categoryId) => {
      setIsLoading(true);
      setSelectedCategories((prevSelected) => {
        const filteredCategoryIds = categories
          .filter((cat) => cat.label !== "-- Select --" && cat.CategoryId !== 0)
          .map((cat) => cat.CategoryId);

        const isAllSelected = prevSelected.includes(0);

        if (categoryId === 0) {
          const updated = isAllSelected ? [] : [0, ...filteredCategoryIds];
          dispatch(writeMessageActions.setSelectedCategories(updated));
          return updated;
        }

        const isSelected = prevSelected.includes(categoryId);
        let updatedSelected = [];

        if (isAllSelected) {
          updatedSelected = prevSelected.filter((id) => id !== 0);
        } else {
          updatedSelected = [...prevSelected];
        }

        if (isSelected) {
          updatedSelected = updatedSelected.filter((id) => id !== categoryId);
        } else {
          updatedSelected.push(categoryId);
        }

        const allSelected = filteredCategoryIds.every((id) =>
          updatedSelected.includes(id)
        );

        const finalSelected = allSelected ? [0, ...filteredCategoryIds] : updatedSelected;
        dispatch(writeMessageActions.setSelectedCategories(finalSelected));
        return finalSelected;

      });
    },
    [categories]
  );

  const finalValueToSend =
    selectedCategories.length === 0
      ? ""
      : selectedCategories.includes(0)
        ? 0
        : selectedCategories.join(",");



  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategories]);


  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    const payload = {
      categoryId: finalValueToSend,
      pageNo: currentPage,
      rowCountPerPage,
      sortBy: "",
    };
    showLoader();
    dispatch(writeMessageActions.LoadEcardList(payload))
      .then((res) => {
        hideLoader(); setIsLoading(false);
        if (!isMounted) return;

        const list = res?.payload?.DataList || [];
        const total = res?.payload?.TotalRecordCount || 0;

        setEcardImages((prev) =>
          currentPage === 1 ? list : [...prev, ...list]
        );
        setTotalEcardsCount(total);
        setIsLoadingMore(false);

        if (list.length < rowCountPerPage) {
          setHasMoreData(false);
        } else {
          setHasMoreData(true);
        }
      })
      .catch((error) => {
        if (isMounted) {
          console.error("Failed to load eCard list:", error);
          setEcardImages([]);
          setIsLoadingMore(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [dispatch, currentPage, selectedCategories]);

  const handleLoadMore = () => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);
    setCurrentPage((prev) => prev + 1);
  };

  const handleEcardClick = (card) => {
    navigate("/public/SendE-Card", { state: { card, EcardInmateID } });
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const fullHeight = document.documentElement.scrollHeight;

      if (hasMoreData && scrollTop + windowHeight >= fullHeight - 100) {
        handleLoadMore();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMoreData, handleLoadMore]);

  return (
    <div className="container-fluid py-4">
      {loader}
      {isLoadingMore && <div className="text-center my-2">{currentLang?.Loading_More}</div>}

      <div className="row col-12">
        <div className="col-sm-4 col-md-4 col-lg-2 border-end">
          <h5 className="fw-bold text-success mb-3">{currentLang?.WriteMessagePage?.Categories}</h5>

          <div
            className="overflow-auto "
            style={{ maxHeight: "calc(100vh - 250px)" }}
          >
            <CategoryCheckbox
              id="all"
              label="Select All"
              checked={selectedCategories.includes(0)}
              onChange={() => handleCheckboxChange(0)}
            />

            {categories
              .filter((cat) => cat.label !== "-- Select --")
              .map((cat, index) => (
                <CategoryCheckbox
                  key={cat.CategoryId}
                  id={`cat-${index}`}
                  label={cat.label}
                  checked={selectedCategories.includes(cat.CategoryId)}
                  onChange={() => handleCheckboxChange(cat.CategoryId)}
                />
              ))}
          </div>
        </div>

        <div className="col-10">
          <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3">
            {ecardImages.length > 0 ? (
              ecardImages.map((card, idx) => (
                <div className="col" key={idx}>
                  <div className="card h-100 text-center border-0 card-fade">
                    {card?.EcardFilePath && <img
                      src={`${process.env.REACT_APP_API_IMAGE_URL}${card?.EcardFilePath}`}
                      alt={card?.EcardFileName}
                      className="card-img-top img-fluid rounded e-card ecard-img"
                      onClick={() => handleEcardClick(card)}
                    />}
                    <div className="card-body py-2 px-1">
                      <h5 className="card-title mb-1 fw-semibold text-truncate text-start">
                        {card?.EcardName?.trim()?.length > 30
                          ? card.EcardName.trim().slice(0, 29) + "..."
                          : card?.EcardName?.trim()?.length > 0
                            ? card?.EcardName
                            : card?.EcardFileName}
                      </h5>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              !isloading && (
                <div className="col-lg-12">
                  <p className="text-center text-success fw-bold">
                    {selectedCategories?.includes(0)
                      ? currentLang?.EcardPage?.NO_Ecard
                      : selectedCategories?.length === 0
                        ? currentLang?.EcardPage?.Pls_Select_Category
                        : currentLang?.EcardPage?.No_ECards_available_in_this_category}
                  </p>
                </div>
              ))}
          </div>

          {hasMoreData && ecardImages?.length > 0 && (
            <div className="d-flex justify-content-center mt-4">
              <div className="div-load-more-data inbox panel footer-load-more">
                <label
                  className="text-decoration-none fs-5 cursor-pointer load-more load-more-button"
                  onClick={handleLoadMore}
                >
                  <i className="fa-solid fa-plus me-1 font-size-14"></i>{currentLang?.buttons?.LoadMore}
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(EcardGallery);
